import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";

const conversationSteps = [
  { role: "customer", text: "Hey I saw your ad about the free strategy call" },
  { role: "ai", text: "hey! yea we're booking those this week. what kind of coaching are you looking for?" },
  { role: "customer", text: "Business coaching, I run a marketing agency" },
  { role: "ai", text: "nice, how long have you been running it? and whats your current revenue roughly" },
  { role: "customer", text: "About 2 years, doing around 15k/month" },
  { role: "ai", text: "ok cool thats a great starting point. we've helped a few agency owners scale past 30k. want to hop on a quick call to see if its a fit? i have thursday at 3pm or friday morning open" },
  { role: "customer", text: "Friday morning works" },
  { role: "ai", text: "booked! friday at 10am. ill send you a reminder thursday night. looking forward to it" },
];

export default function PhoneMockup() {
  const [visibleCount, setVisibleCount] = useState(0);
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [visibleCount, isTyping]);

  const handleNext = () => {
    if (visibleCount >= conversationSteps.length) {
      setVisibleCount(0);
      setIsTyping(false);
      return;
    }

    const nextMsg = conversationSteps[visibleCount];
    if (nextMsg.role === "ai") {
      setIsTyping(true);
      setTimeout(() => {
        setIsTyping(false);
        setVisibleCount((c) => c + 1);
      }, 1200);
    } else {
      setVisibleCount((c) => c + 1);
    }
  };

  // Auto-advance the first message on mount
  useEffect(() => {
    const timer = setTimeout(() => {
      setVisibleCount(1);
    }, 800);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="relative" data-testid="phone-mockup">
      {/* Phone Frame */}
      <div className="relative w-[280px] sm:w-[300px] h-[580px] sm:h-[600px] rounded-[3rem] border-[3px] border-zinc-700 bg-black overflow-hidden shadow-2xl">
        {/* Notch / Dynamic Island */}
        <div className="absolute top-2 left-1/2 -translate-x-1/2 w-[90px] h-[25px] bg-black rounded-full z-20 border border-zinc-800" />

        {/* Status bar */}
        <div className="absolute top-0 left-0 right-0 h-12 bg-black/80 backdrop-blur-sm z-10 flex items-end justify-center pb-1">
          <span className="text-zinc-400 text-[10px] font-medium">Coach Sarah | Strategy Calls</span>
        </div>

        {/* Messages area */}
        <div className="absolute top-12 left-0 right-0 bottom-16 overflow-y-auto px-3 pt-4 pb-2 space-y-2" style={{ scrollbarWidth: "none" }}>
          {/* Date stamp */}
          <div className="text-center mb-3">
            <span className="text-zinc-600 text-[10px]">Today 2:34 PM</span>
          </div>

          <AnimatePresence>
            {conversationSteps.slice(0, visibleCount).map((msg, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 12, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.3, ease: "easeOut" }}
                className={`flex ${msg.role === "customer" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[80%] px-3.5 py-2 rounded-2xl text-[13px] leading-relaxed ${
                    msg.role === "customer"
                      ? "blue-bubble text-white rounded-br-md"
                      : "grey-bubble text-zinc-100 rounded-bl-md"
                  }`}
                >
                  {msg.text}
                </div>
              </motion.div>
            ))}
          </AnimatePresence>

          {/* Typing indicator */}
          {isTyping && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex justify-start"
            >
              <div className="grey-bubble px-4 py-3 rounded-2xl rounded-bl-md flex gap-1.5">
                <span className="typing-dot w-1.5 h-1.5 bg-zinc-400 rounded-full inline-block" />
                <span className="typing-dot w-1.5 h-1.5 bg-zinc-400 rounded-full inline-block" />
                <span className="typing-dot w-1.5 h-1.5 bg-zinc-400 rounded-full inline-block" />
              </div>
            </motion.div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Bottom bar - tap to continue */}
        <div className="absolute bottom-0 left-0 right-0 h-16 bg-black/90 backdrop-blur-sm border-t border-zinc-800 flex items-center justify-center">
          <button
            data-testid="phone-next-button"
            onClick={handleNext}
            className="text-blue-400 text-xs font-medium hover:text-blue-300 transition-colors px-4 py-2 rounded-full bg-zinc-900 border border-zinc-700 hover:border-zinc-600"
          >
            {visibleCount >= conversationSteps.length ? "Replay" : "Tap to continue"}
          </button>
        </div>
      </div>

      {/* Glow effect behind phone */}
      <div className="absolute -inset-8 bg-blue-500/10 rounded-full blur-3xl -z-10 pointer-events-none" />
    </div>
  );
}
